import React from 'react';
import { Button, makeStyles, Theme } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';

export type AdminSection = 'analytics' | 'invite' | 'permissions';

type AdminSideNavigationProps = {
  activeSection: AdminSection;
  onSectionChange: (section: AdminSection) => void;
  onBack: () => void;
  canManageAdmins: boolean;
};

const useStyles = makeStyles((theme: Theme) => ({
  sideNavigation: {
    width: 230,
    flexShrink: 0,
    padding: '34px 0 8px',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.grey[100],
    borderRight: `1px solid ${theme.palette.grey[300]}`,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      padding: 0,
      flexDirection: 'row',
      borderRight: 0,
      borderBottom: `1px solid ${theme.palette.grey[300]}`,
    },
  },
  navigationButton: {
    width: '100%',
    minHeight: 52,
    padding: '12px 28px',
    borderRadius: 0,
    justifyContent: 'flex-start',
    textTransform: 'none',
    fontSize: 16,
    fontWeight: 600,
    [theme.breakpoints.down('sm')]: {
      width: 'auto',
      flex: 1,
      justifyContent: 'center',
    },
  },
  activeNavigationButton: {
    color: theme.palette.secondary.main,
    backgroundColor: theme.palette.common.white,
    borderLeft: `4px solid ${theme.palette.secondary.main}`,
    '&:hover': {
      backgroundColor: theme.palette.common.white,
    },
    [theme.breakpoints.down('sm')]: {
      borderLeft: 0,
      borderBottom: `4px solid ${theme.palette.secondary.main}`,
    },
  },
  backButton: {
    marginTop: 'auto',
    [theme.breakpoints.down('sm')]: {},
  },
  backIcon: {
    marginRight: 8,
  },
}));

const AdminSideNavigation = ({
  activeSection,
  onSectionChange,
  onBack,
  canManageAdmins,
}: AdminSideNavigationProps): JSX.Element => {
  const classes = useStyles();

  return (
    <nav className={classes.sideNavigation} aria-label="Admin portal sections">
      <Button
        className={`${classes.navigationButton} ${activeSection === 'analytics' ? classes.activeNavigationButton : ''}`}
        onClick={() => onSectionChange('analytics')}
        aria-current={activeSection === 'analytics' ? 'page' : undefined}
      >
        Export Analytics
      </Button>
      <Button
        className={`${classes.navigationButton} ${activeSection === 'invite' ? classes.activeNavigationButton : ''}`}
        onClick={() => onSectionChange('invite')}
        aria-current={activeSection === 'invite' ? 'page' : undefined}
      >
        Invite Form
      </Button>
      {canManageAdmins && (
        <Button
          className={`${classes.navigationButton} ${
            activeSection === 'permissions' ? classes.activeNavigationButton : ''
          }`}
          onClick={() => onSectionChange('permissions')}
          aria-current={activeSection === 'permissions' ? 'page' : undefined}
        >
          Admin Permissions
        </Button>
      )}
      <Button className={`${classes.navigationButton} ${classes.backButton}`} onClick={onBack}>
        <ArrowBackIcon className={classes.backIcon} fontSize="small" />
        Back
      </Button>
    </nav>
  );
};

export default AdminSideNavigation;
